import { useTranslation } from 'react-i18next';
import { G, Rect, Text as SvgText, Circle, Line } from 'react-native-svg';
import moment from 'moment';
import { isNumber } from 'lodash';

import { ThresholdColors } from '../consts';
import { tempValue } from '../_helper';

type Metricset = { startTime: number | null; endTime: number | null; value: number };

type Thresholds = {
  LowerRed?: number;
  LowerYellow?: number;
  LowerGreen?: number;
  UpperGreen?: number;
  UpperYellow?: number;
  UpperRed?: number;
};

type ChartTooltipFlyoutProps = {
  // Victory-injected at render time
  x?: number;
  y?: number;
  datum?: { x: number; y: number | null };
  active?: boolean;
  text?: string | string[];

  // Custom props passed by the parent chart
  signalDatasets?: Metricset[];
  doorDatasets?: Metricset[];
  hasDoor?: boolean;
  thresholds?: Thresholds;
  chartWidth?: number;
  // Y-coordinate (in chart coordinates) where the X-axis sits — used to
  // anchor the crosshair line from the hovered point down to the axis.
  axisY?: number;
};

// At any state-change boundary, createMetricsets emits two segments that
// share an endpoint: the outgoing segment closes with endTime = T and the
// incoming segment opens with startTime = T. The signal/door bars below
// the chart draw segments in chronological order, so the LATER segment
// overdraws the earlier one at x = T. The tooltip's state read has to
// match that visual — otherwise hovering exactly on a state-change time
// (which Voronoi snaps to, since temperature samples land on the same
// 5-min cadence the API uses for door / signal changes) shows
// "Open" / "Online" in the tooltip while the bar already reads
// closed / offline at that pixel. Picking the LATEST matching segment
// makes the lookup match the bar's overdraw order.
const lookupAt = (datasets: Metricset[] | undefined, t: number): number | undefined => {
  if (!datasets) return undefined;
  let match: Metricset | undefined;
  for (const seg of datasets) {
    if (seg.startTime != null && seg.endTime != null && seg.startTime <= t && t <= seg.endTime) {
      match = seg;
    }
  }
  return match?.value;
};

const colorForTemp = (val: number, thresholds?: Thresholds): string => {
  if (!thresholds) return ThresholdColors.Blue;
  if (isNumber(thresholds.UpperRed) && val >= thresholds.UpperRed) return ThresholdColors.Red;
  if (isNumber(thresholds.UpperYellow) && val >= thresholds.UpperYellow) return ThresholdColors.Yellow;
  if (isNumber(thresholds.UpperGreen) && val >= thresholds.UpperGreen) return ThresholdColors.Green;
  if (isNumber(thresholds.LowerRed) && val <= thresholds.LowerRed) return ThresholdColors.Red;
  if (isNumber(thresholds.LowerYellow) && val <= thresholds.LowerYellow) return ThresholdColors.Yellow;
  if (isNumber(thresholds.LowerGreen) && val <= thresholds.LowerGreen) return ThresholdColors.Green;
  return ThresholdColors.Blue;
};

// Bar colors — match the signal/door bands rendered below the chart
const SIGNAL_ONLINE = '#14B8A6'; // teal-500
const SIGNAL_OFFLINE = '#5EEAD4'; // teal-300 (visually paler)
const DOOR_CLOSED = '#FAE8FF'; // fuchsia-100
const DOOR_OPEN = '#D946EF'; // fuchsia-500

export const ChartTooltipFlyout = (props: ChartTooltipFlyoutProps) => {
  const { t } = useTranslation();
  const { x = 0, y = 0, datum, active, text, signalDatasets, doorDatasets, hasDoor, thresholds, chartWidth, axisY } = props;

  // Suppress when not active, no datum, no temp value, or when label was blanked
  // by the parent (e.g. min/max sentinels)
  if (!active || !datum || datum.y == null) return null;
  if (Array.isArray(text) ? text.every(v => !v) : !text) return null;

  const ts = datum.x;
  const tempColor = colorForTemp(datum.y as number, thresholds);
  const signalVal = lookupAt(signalDatasets, ts);
  const doorVal = hasDoor ? lookupAt(doorDatasets, ts) : undefined;

  const padding = 10;
  const headerH = 22;
  const rowH = 20;
  const rows = 2 + (hasDoor ? 1 : 0);
  const width = 196;
  const height = headerH + rows * rowH + padding * 2;

  // Position above and slightly right of the point; flip to the left when
  // close to the right edge so the tooltip stays visible.
  const overflowsRight = chartWidth != null && x + 12 + width > chartWidth;
  const flyoutX = overflowsRight ? x - 12 - width : x + 12;
  const flyoutY = Math.max(4, y - height - 12);

  const signalLabel = signalVal === 1 ? t('store_equipment:online') : signalVal === 0 ? t('store_equipment:offline') : '—';

  // Door API uses Value=1 (below trigger = closed) and Value=2 (at/above
  // trigger = open). Treat anything that isn't 1 as Open.
  const doorLabel = doorVal == null ? '—' : doorVal === 1 ? t('store_equipment:closed') : t('store_equipment:open');

  const fontFamily = 'Inter, sans-serif';

  // Crosshair: vertical dashed line from the hovered point down to the
  // X-axis, plus a threshold-colored dot on the temperature line at the
  // hovered position. Both make it unambiguous which timestamp the tooltip
  // belongs to.
  const crosshairBottom = typeof axisY === 'number' ? axisY : y + 200;

  const doorRow = hasDoor ? (
    <G>
      <Circle cx={padding + 6} cy={padding + headerH + rowH * 2 + 10} r={5} fill={doorVal === 1 ? DOOR_CLOSED : DOOR_OPEN} />
      <SvgText x={padding + 18} y={padding + headerH + rowH * 2 + 14} fontSize={14} fontWeight={600} fill="#344054" fontFamily={fontFamily}>
        {`${t('store_equipment:door')}: ${doorLabel}`}
      </SvgText>
    </G>
  ) : null;

  // CRITICAL: every SVG primitive in this flyout must have pointer-events="none".
  // The voronoi container relies on an invisible capture overlay to detect
  // mousemove; if any flyout element captures pointer events it interrupts the
  // hover detection and the user has to mousedown-drag to keep the active state.
  return (
    <G pointerEvents="none">
      <Line x1={x} y1={y} x2={x} y2={crosshairBottom} stroke="#98A2B3" strokeWidth={1} strokeDasharray="3,3" pointerEvents="none" />
      <Circle cx={x} cy={y} r={5} fill={tempColor} stroke="white" strokeWidth={1.5} pointerEvents="none" />

      <G transform={`translate(${flyoutX}, ${flyoutY})`} pointerEvents="none">
        <Rect x={0} y={0} width={width} height={height} rx={6} ry={6} fill="white" stroke={tempColor} strokeWidth={1.5} />

        <SvgText x={padding} y={padding + 14} fontSize={13} fontWeight={700} fill="#101828" fontFamily={fontFamily}>
          {moment(ts).format('MMM D, h:mm A')}
        </SvgText>

        <Circle cx={padding + 6} cy={padding + headerH + 10} r={5} fill={tempColor} />
        <SvgText x={padding + 18} y={padding + headerH + 14} fontSize={14} fontWeight={600} fill="#344054" fontFamily={fontFamily}>
          {`${t('store_equipment:temp')}: ${tempValue(datum.y as number)}`}
        </SvgText>

        <Circle cx={padding + 6} cy={padding + headerH + rowH + 10} r={5} fill={signalVal === 1 ? SIGNAL_ONLINE : SIGNAL_OFFLINE} />
        <SvgText x={padding + 18} y={padding + headerH + rowH + 14} fontSize={14} fontWeight={600} fill="#344054" fontFamily={fontFamily}>
          {`${t('store_equipment:signal')}: ${signalLabel}`}
        </SvgText>

        {doorRow}
      </G>
    </G>
  );
};
